import React, {createContext, useContext} from 'react';
import {assert} from 'assert-ts';
import type {ThesaurusId} from 'domainTypes';
import type {CodeListMap} from 'schemaDefinition/types';
import type {CodeListId} from 'services/codelists/types';
import type {ThesaurusesMap} from 'services/thesaurus/types';
import type {LinkedRoleCodeListId} from 'schemaDefinition/types/linkTypes';

/**
 * Data-access seam between the schema rendering layer and the host application.
 *
 * The schema layer needs codelists and thesauruses that the host owns in its
 * (Redux) store. Rather than reaching into the store directly, it consumes these
 * accessor hooks via context, so the host supplies the concrete implementation.
 * This keeps the schema layer decoupled from app state management.
 */
export type SchemaDataAccess = {
  /** Resolve a set of codelist ids to a map of loaded codelists. */
  useCodelists: (ids: (CodeListId | LinkedRoleCodeListId)[]) => CodeListMap;
  /** Resolve a set of thesaurus ids to a map of loaded thesauruses. */
  useThesauruses: (ids: ThesaurusId[]) => ThesaurusesMap;
};

const SchemaDataContext = createContext<SchemaDataAccess | undefined>(
  undefined,
);

export const SchemaDataProvider: React.FC<
  React.PropsWithChildren<{value: SchemaDataAccess}>
> = ({value, children}) => (
  <SchemaDataContext.Provider value={value}>
    {children}
  </SchemaDataContext.Provider>
);

export const useSchemaDataAccess = (): SchemaDataAccess => {
  return assert(
    useContext(SchemaDataContext),
    'useSchemaDataAccess: missing SchemaDataProvider. The host application must ' +
      'provide a SchemaDataAccess implementation.',
  );
};
